/**
 * 융합 일일 카운터 — 순수 로직 (Phaser 비의존, 단위 테스트 대상). DailyShopController와 동형.
 *
 * 비용 규칙(사용자 명시): **첫 회는 광고**, **2회째부터 다이아 고정**(값은 global.tsv fusionDiamondCost), 횟수는 **매일 자정 리셋**.
 * 자정 리셋 트리거는 @yjsgames/daily-reset의 DailyResetService("module-fusion" 키)가 구동하고,
 * 리셋 발생 시 GameScene이 onReset 콜백에서 applyReset()을 호출한다 (일일상점 applyReset과 동형).
 * 비용 차감(다이아)·광고 시청·보상 지급은 GameScene 책임 — 본 컨트롤러는 사용 횟수 카운터만 관리.
 */

/** 융합 영속 상태 — 자정 리셋 시각은 dailyResets["module-fusion"]가 SSoT라 여기엔 없다. */
export interface ModuleFusionState {
  usedCount: number;
}

/** 융합 1회 비용 — 무료(광고 제거 IAP) / 광고(첫 회) / 다이아(2회째부터 고정). */
export type FusionCost =
  | { readonly kind: "free" }
  | { readonly kind: "ad" }
  | { readonly kind: "diamond"; readonly amount: number };

/** 기본 다이아 비용 — 데이터 누락 시 폴백(실값은 global.tsv fusionDiamondCost 주입). */
export const DEFAULT_FUSION_DIAMOND_COST = 30;

export class ModuleFusionController {
  private usedCount: number;
  private readonly diamondCost: number;
  /** 진행 중 융합 플래그 (런타임 전용, 영속 X) — 광고 비동기 콜백 대기 사이 재진입을 막아 무료 융합 레이스를 차단한다. */
  private pending = false;

  constructor(initial?: Partial<ModuleFusionState>, diamondCost: number = DEFAULT_FUSION_DIAMOND_COST) {
    this.usedCount = Math.max(0, Math.floor(initial?.usedCount ?? 0));
    this.diamondCost = Math.max(0, Math.floor(diamondCost));
  }

  /** 오늘 융합한 횟수 (자정 리셋 대상). */
  get usedToday(): number {
    return this.usedCount;
  }

  /**
   * 융합 시작 예약 — 이미 진행 중(광고 콜백 대기 등)이면 false로 거절(재진입 차단).
   * **Why:** cost()는 count 0이면 광고를 반환한다. 광고 콜백 대기 사이 두 번째 융합이 들어오면 둘 다 count 0을
   * 보고 무료(광고) 융합되는 레이스가 생긴다 — tryBegin이 단일 진행을 강제해 차단. 호출자는 모든 종료 경로에서 end().
   */
  tryBegin(): boolean {
    if (this.pending) return false;
    this.pending = true;
    return true;
  }

  /** 융합 종료(성공/취소/실패/광고 스킵) — 진행 플래그 해제. tryBegin과 짝. */
  end(): void {
    this.pending = false;
  }

  /**
   * 현재 융합 1회 비용 — count 0이면 광고(adFree면 무료), count≥1이면 다이아 고정.
   * adFree(광고 제거 IAP)는 **첫 회 광고만** 스킵 — 2회째부터는 동일하게 다이아.
   */
  cost(adFree = false): FusionCost {
    if (this.usedCount === 0) return adFree ? { kind: "free" } : { kind: "ad" };
    return { kind: "diamond", amount: this.diamondCost };
  }

  /** 융합 성공 시 호출 — 사용 횟수 +1 (다음 비용 단계 상승). 비용 지불은 호출자 책임. */
  markUsed(): void {
    this.usedCount += 1;
  }

  /** 자정 리셋 — @yjsgames/daily-reset onReset 콜백에서 호출. 사용 횟수 0 복귀 → 다시 첫 회 광고. */
  applyReset(): void {
    this.usedCount = 0;
  }

  /** 영속 상태 스냅샷 — SaveData 직렬화용 (생성자 initial로 round-trip 복원). */
  toState(): ModuleFusionState {
    return { usedCount: this.usedCount };
  }
}
